import React from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, Button, Select, message } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import { MainContext } from '../../contexts/mainContext';
import { getClient } from '../../apollo';
import {
  CreatePassengerDocument,
  CreatePassengerMutation,
  CreatePassengerMutationVariables,
  GetPassengersDocument,
  GetPassengersQuery,
  GetPassengersQueryVariables,
} from '../../graphql/datamodel.gen';
import { validateEmail } from '../../utils/email';
import { validatePhone } from '../../utils/phone';

interface IState {
  loading: boolean;
}

interface IProps extends FormComponentProps {
  onSubmit?: (
    success: boolean,
    passenger?: { id?: string; email?: string; phone?: string; name?: string },
  ) => any;
}

const createPassenger = async (
  email: string,
  name: string,
  phone?: string,
): Promise<CreatePassengerMutation['createPassenger'] | undefined> => {
  try {
    const response = await getClient().mutate<
      CreatePassengerMutation,
      CreatePassengerMutationVariables
    >({
      mutation: CreatePassengerDocument,
      variables: {
        name,
        phone,
        email,
      },
      update: (proxy, mutationResult) => {
        try {
          const previousPassengers = getClient().readQuery<
            GetPassengersQuery,
            GetPassengersQueryVariables
          >({
            query: GetPassengersDocument,
          });

          const newPassengerList = [
            ...previousPassengers.passengers,
            mutationResult.data.createPassenger,
          ];

          getClient().writeQuery({
            query: GetPassengersDocument,
            data: {
              passengers: newPassengerList,
            },
          });
        } catch (e) {
          getClient().writeQuery({
            query: GetPassengersDocument,
            data: {
              passengers: [mutationResult.data.createPassenger],
            },
          });
        } finally {
          // @ts-ignore
          getClient().queryManager.broadcastQueries();
        }
      },
    });

    return response.data?.createPassenger;
  } catch (e) {
    console.error('Error while creating passenger', e);
    return undefined;
  }
};

class GenericCreateNewPassengerForm extends React.Component<IProps, IState> {
  static contextType = MainContext;
  state = {
    loading: false,
  };

  handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values) => {
      if (!err) {
        const { email, phone, name } = values;
        try {
          this.setState({
            loading: true,
          });
          message.loading(
            {
              content: 'Creando pasajero',
              key: 'passenger_create',
            },
            0,
          );

          const passenger = await createPassenger(email, name, phone);
          this.setState({
            loading: false,
          });

          if (passenger) {
            message.success(
              {
                content: 'Pasajero creado correctamente.',
                key: 'passenger_create',
              },
              4,
            );
          } else {
            message.error(
              {
                content: 'Error al crear pasajero',
                key: 'passenger_create',
              },
              4,
            );
          }

          if (this.props.onSubmit) {
            this.props.onSubmit(!!passenger, passenger);
          }

          return;
        } catch (error) {
          console.error('Error creating passenger', error);
          if (this.props.onSubmit) {
            this.props.onSubmit(false);
          }
        } finally {
          setTimeout(() => {
            message.destroy();
          }, 2000);
        }
      }
    });
  };

  emailValidator = async (
    rule: any,
    value: any,
    callback: (arg0?: string) => void,
  ) => {
    const email = this.props.form.getFieldValue('email');

    const invalid = await validateEmail(email, {
      isRequired: {
        enabled: false,
      },
      checkAgainstFirebase: {
        enabled: false,
      },
    });

    if (invalid) {
      callback(invalid);
    } else {
      callback();
    }
  };

  phoneValidator = (_: any, __: any, callback: (...args: any[]) => void) => {
    // @ts-ignore
    const { form } = this.props;
    const phone = form.getFieldValue('phone');

    const invalid = validatePhone(phone, {
      isRequired: {
        enabled: false,
      },
    });

    if (invalid) {
      callback(invalid);
    } else {
      callback();
    }
  };

  render() {
    // @ts-ignore
    const { getFieldDecorator } = this.props.form;

    return (
      <Form onSubmit={this.handleSubmit} layout="vertical" hideRequiredMark>
        <Form.Item label="Nombre y apellido(s)">
          {getFieldDecorator('name', {
            rules: [
              {
                required: true,
                message: 'Por favor, introduce nombre y apellido(s)',
              },
            ],
          })(<Input />)}
        </Form.Item>
        <Form.Item label="Correo electrónico">
          {getFieldDecorator('email', {
            rules: [
              {
                required: true,
                message: 'Por favor, introduce un correo electrónico',
              },
              {
                validator: this.emailValidator,
              },
            ],
          })(<Input />)}
        </Form.Item>
        <Form.Item label="Teléfono móvil">
          {getFieldDecorator('phone', {
            rules: [
              {
                required: false,
              },
              {
                validator: this.phoneValidator,
              },
            ],
          })(<Input style={{ width: '100%' }} />)}
        </Form.Item>

        <Form.Item>
          <Button
            loading={this.state.loading}
            type="primary"
            htmlType="submit"
            className="login-form-button"
          >
            Crear
          </Button>
        </Form.Item>
      </Form>
    );
  }
}

export const CreateNewPassengerForm = Form.create<IProps>()(
  GenericCreateNewPassengerForm,
);
